import { analogRecipeChart } from './analog'
import type { DentinRecipe } from './dentin'
import { fromRecipe } from './fromRecipe'
import { type LabColorInput, toLabColor } from './toLabColor'

export const ALL_IN_ONE_RECIPE_PATH = 'generate' as const
export const ALL_IN_ONE_STORED_TYPE = 'generic'

export const ALL_IN_ONE_REGIONS = ['cervical', 'middle', 'incisal'] as const
export type AllInOneRegion = (typeof ALL_IN_ONE_REGIONS)[number]

const SINGLE_DENTIN_HIDDEN_KEYS = new Set([
  'skinenameltype_cervical',
  'skinenameltype_incisal',
  'method',
  'portionertype',
  'prepcolor_cervical',
  'prepcolor_incisal',
  'targetcervical',
  'targetincisal',
  'targetcolor_cervical',
  'targetcolor_incisal',
  'availspace_cervical',
  'availspace_incisal',
  'generation_id',
  'allow_mixing',
])

const SHARED_HIDDEN_KEYS = new Set([
  'skinenameltype_cervical',
  'skinenameltype_incisal',
  'method',
  'portionertype',
  'generation_id',
  'allow_mixing',
])

const toSpaceNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined
  const n = Number(String(value).replace(/mm/gi, '').trim())
  return Number.isFinite(n) ? n : undefined
}

const assignIfPresent = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  if (value == null || value === '') return
  payload[key] = value
}

const assignSpace = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  const space = toSpaceNumber(value)
  if (space != null) payload[key] = space
}

const assignLabColor = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  const lab = toLabColor(value as LabColorInput)
  if (lab) payload[key] = lab
}

const assignLabOrGuide = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  if (value == null || value === '') return
  if (typeof value === 'string') {
    payload[key] = value
    return
  }
  assignLabColor(payload, key, value)
}

type RegionValue<T> = {
  cervical?: T | null
  middle?: T | null
  incisal?: T | null
}

export const buildAllInOnePayload = ({
  recipeType,
  indication,
  ceramicbrand,
  frameworkbrand,
  stainingbrand,
  naturalDieMaterial,
  protocol,
  skinenameltype,
  targetColors,
  prepColors,
  availspace,
  includePrepColors,
}: {
  recipeType?: string | null
  indication?: string | null
  ceramicbrand?: string | null
  frameworkbrand?: string | null
  stainingbrand?: string | null
  naturalDieMaterial?: string | null
  protocol?: string | null
  skinenameltype?: string | null
  targetColors?: RegionValue<unknown>
  prepColors?: RegionValue<unknown>
  availspace?: RegionValue<unknown>
  includePrepColors?: boolean
}): Record<string, unknown> => {
  const payload: Record<string, unknown> = {}
  const isTripleZone = recipeType === 'triple dentin'

  assignIfPresent(payload, 'recipe_type', recipeType)
  assignIfPresent(payload, 'indication', indication)
  assignIfPresent(payload, 'ceramicbrand', ceramicbrand)
  assignIfPresent(payload, 'frameworkbrand', frameworkbrand)
  assignIfPresent(payload, 'stainingbrand', stainingbrand)
  assignIfPresent(payload, 'natural_die_material', naturalDieMaterial)
  assignIfPresent(payload, 'protocol', protocol)
  assignIfPresent(payload, 'skinenameltype', skinenameltype)
  assignIfPresent(payload, 'skinenameltype_cervical', skinenameltype)
  assignIfPresent(payload, 'skinenameltype_middle', skinenameltype)
  assignIfPresent(payload, 'skinenameltype_incisal', skinenameltype)

  assignLabOrGuide(payload, 'targetcolor_middle', targetColors?.middle)
  assignSpace(payload, 'availspace_middle', availspace?.middle)

  if (includePrepColors) {
    assignLabOrGuide(payload, 'prepcolor_middle', prepColors?.middle)
  }

  if (isTripleZone) {
    assignLabOrGuide(payload, 'targetcolor_cervical', targetColors?.cervical)
    assignLabOrGuide(payload, 'targetcolor_incisal', targetColors?.incisal)
    assignSpace(payload, 'availspace_cervical', availspace?.cervical)
    assignSpace(payload, 'availspace_incisal', availspace?.incisal)
    if (includePrepColors) {
      assignLabOrGuide(payload, 'prepcolor_cervical', prepColors?.cervical)
      assignLabOrGuide(payload, 'prepcolor_incisal', prepColors?.incisal)
    }
  }

  return payload
}

export const getAllInOneCeramicBrand = (
  output?: {
    provided_input?: { ceramicbrand?: string; brand?: string }
  } | null,
): string | undefined =>
  output?.provided_input?.ceramicbrand ?? output?.provided_input?.brand

export const getDentinLayerAdvice = (
  output: any,
  region: AllInOneRegion,
): DentinRecipe | undefined =>
  output?.[`dentinlayer_advice_${region}`] ??
  output?.dentinlayer_advice?.[`dentinrecipes_${region}`]

export const getEnamelLayerAdvice = (
  output: any,
  region: AllInOneRegion,
): string | undefined =>
  output?.[`enamellayer_advice_${region}`] ??
  output?.enamellayer_advice?.[`enamel_${region}`]

export const getPrepAdvice = (output: any, region: AllInOneRegion): unknown => {
  const v2 = output?.[`prep_advice_${region}`]
  if (v2) return v2
  const nested = output?.prep_advice
  if (!nested) return undefined
  return {
    component_names: nested[`prep_names_${region}`],
    ratios: nested[`prep_ratios_${region}`],
    prep_names: nested[`prep_names_${region}`],
    prep_ratios: nested[`prep_ratios_${region}`],
  }
}

export const getOpaquerAdvice = (
  output: any,
  region: AllInOneRegion,
): unknown => {
  const v2 = output?.[`opaquer_advice_${region}`]
  if (v2) return v2
  const nested = output?.internallayer_advice
  const names = nested?.[`opaquers_${region}`]
  const ratios = nested?.[`opaquers_ratios_${region}`]
  if (!names && !ratios) return undefined
  return {
    component_names: names,
    ratios,
    powders: names,
    simplified_ratios: ratios,
  }
}

export const getMaskingRecipe = (
  output: any,
  region: AllInOneRegion,
): unknown => output?.[`masking_recipe_${region}`]

export const getFrameworkAdviceList = (output: any): any[] => {
  const advice = output?.internallayer_advice
  if (Array.isArray(advice)) return advice
  return advice ? [advice] : []
}

export const hasPrepAdvice = (output: any): boolean =>
  ALL_IN_ONE_REGIONS.some((region) => {
    const advice = output?.[`prep_advice_${region}`]
    return Array.isArray(advice) ? advice.length > 0 : Boolean(advice)
  }) || Boolean(output?.prep_advice)

export const hasDentinAdvice = (output: any): boolean =>
  ALL_IN_ONE_REGIONS.some((region) =>
    Boolean(getDentinLayerAdvice(output, region)),
  )

export const hasOpaquerAdvice = (output: any): boolean =>
  ALL_IN_ONE_REGIONS.some((region) => {
    const advice = getOpaquerAdvice(output, region)
    if (Array.isArray(advice)) return advice.length > 0
    return Boolean(advice)
  })

export const hasMaskingAdvice = (output: any): boolean =>
  ALL_IN_ONE_REGIONS.some((region) => Boolean(getMaskingRecipe(output, region)))

export const dentinLayerView = (
  item?: {
    component_names?: string[]
    powders?: string[]
    ratios?: Array<number | string>
    simplified_ratios?: Array<number | string>
    opacity_category?: string | null
  } | null,
) => {
  const view = fromRecipe(item)
  const names = view.names.length ? view.names : (item?.powders ?? [])
  const ratios = view.ratios.length
    ? view.ratios
    : (item?.simplified_ratios ?? item?.ratios ?? [])
  const opacityCategory =
    view.opacityCategory ??
    (typeof item?.opacity_category === 'string'
      ? item.opacity_category
      : undefined)

  return {
    names,
    ratios,
    opacityCategory,
  }
}

export const allInOneRecipeChart = analogRecipeChart

export const getProvidedInputTableKeys = (
  providedInput?: Record<string, unknown> | null,
): string[] => {
  const keys = Object.keys(providedInput ?? {})
  const hidden =
    providedInput?.recipe_type === 'single dentin'
      ? SINGLE_DENTIN_HIDDEN_KEYS
      : SHARED_HIDDEN_KEYS
  return keys.filter((key) => !hidden.has(key))
}

export const selectDentinRegion = (
  dentin: DentinRecipe | undefined,
  index: number,
): DentinRecipe | undefined => {
  if (!dentin) return undefined
  const layer1 = dentin.recipe_layer1?.[index] ?? null
  const layer2 = dentin.recipe_layer2?.[index] ?? null
  return {
    ...dentin,
    recipe_layer1: layer1 ? [layer1] : undefined,
    recipe_layer2: layer2 ? [layer2] : undefined,
  }
}
